/**
 * Inventory domain service.
 *
 * Wraps the trusted inventory RPCs (paginated view, stock adjustment, opening
 * stock import). The append-only ledger and balance materialisation remain
 * backend-owned; this layer only exposes the operations and normalises errors.
 */

import { callRpc } from "@/lib/api/rpc";

export interface InventoryPageParams {
  orgId: string;
  search: string | null;
  locationId: string | null;
  stockFilter: string;
  page: number;
  pageSize: number;
}

export function getInventoryPage<T = Record<string, unknown>>(params: InventoryPageParams): Promise<T[]> {
  return callRpc<T[]>("get_inventory_page", {
    p_org_id: params.orgId,
    p_search: params.search,
    p_location_id: params.locationId,
    p_stock_filter: params.stockFilter,
    p_page: params.page,
    p_page_size: params.pageSize,
  });
}

export interface AdjustmentParams {
  productId: string;
  locationId: string;
  qtyChange: number;
  reason: string | null;
  variantId: string | null;
  idempotencyKey?: string;
}

export function applyInventoryAdjustment(params: AdjustmentParams): Promise<void> {
  return callRpc("apply_inventory_adjustment", {
    p_product_id: params.productId,
    p_location_id: params.locationId,
    p_qty_change: params.qtyChange,
    p_reason: params.reason,
    p_variant_id: params.variantId,
    p_idempotency_key: params.idempotencyKey ?? undefined,
  });
}

export interface SetQuantityParams {
  productId: string;
  locationId: string;
  qty: number;
  reason: string | null;
  variantId: string | null;
  idempotencyKey?: string;
}

/** Set on-hand to an exact quantity. Delta is computed from locked on-hand in the RPC. */
export function applyInventorySetQuantity(params: SetQuantityParams): Promise<{ ok?: boolean; qty_change?: number; on_hand?: number }> {
  return callRpc("apply_inventory_set_quantity", {
    p_product_id: params.productId,
    p_location_id: params.locationId,
    p_qty: params.qty,
    p_reason: params.reason,
    p_variant_id: params.variantId,
    p_idempotency_key: params.idempotencyKey ?? undefined,
  });
}

export interface OpeningInventoryLine {
  product_id: string;
  variant_id: string | null;
  location_id: string;
  qty: number;
  unit_cost: number | null;
}

export interface OpeningInventoryResult {
  imported: number;
  failed: number;
}

export function importOpeningInventory(
  orgId: string,
  lines: OpeningInventoryLine[],
  confirmExisting: boolean,
  idempotencyKey: string,
): Promise<OpeningInventoryResult> {
  return callRpc<OpeningInventoryResult>("import_opening_inventory", {
    p_org_id: orgId,
    p_lines: lines,
    p_confirm_existing: confirmExisting,
    p_idempotency_key: idempotencyKey,
  });
}